import { z } from 'zod';
/** DC-388 — ADR / coding standard lifecycle projection */
export const governanceLifecycleStatusSchema = z.enum([
    'draft',
    'proposed',
    'accepted',
    'superseded',
]);
export const standardsApplicabilityGapStateSchema = z.enum([
    'ready',
    'pending',
    'pending_traceability',
    'not_configured',
    'forbidden',
]);
export const artifactKindSchema = z.enum(['adr', 'coding_standard']);
export const applicabilitySourceSchema = z.enum([
    'configured_binding',
    'trace_graph',
    'engineering_memory_suggestion',
]);
export const governanceRoutingKindSchema = z.enum(['acceptance_required', 'conflict', 'exception']);
export const governanceRoutingRequestBodySchema = z
    .object({
    kind: governanceRoutingKindSchema,
    policyRefs: z.object({
        adrIds: z.array(z.string().uuid()).default([]),
        codingStandardIds: z.array(z.string().uuid()).default([]),
    }),
    clientIdempotencyKey: z.string().min(1).max(128).optional(),
})
    .strict();
export const governanceRoutingResolveBodySchema = z
    .object({
    status: z.enum(['resolved', 'withdrawn']),
    resolutionSummary: z.string().max(1024).optional(),
})
    .strict();
export const workItemStandardsBindingWriteBodySchema = z
    .object({
    artifactKind: artifactKindSchema,
    artifactId: z.string().uuid(),
    rationale: z.string().max(4000).optional(),
})
    .strict();
export const adrLifecyclePatchBodySchema = z
    .object({
    lifecycleStatus: governanceLifecycleStatusSchema,
    expectedUpdatedAt: z.string().datetime().optional(),
})
    .strict();
export const codingStandardLifecyclePatchBodySchema = z
    .object({
    lifecycleStatus: governanceLifecycleStatusSchema,
    expectedUpdatedAt: z.string().datetime().optional(),
})
    .strict();
/** DC-388 — `GET …/govern/standards/catalog` row shapes (camelCase JSON). */
export const standardsCatalogAdrRowSchema = z.object({
    id: z.string().uuid(),
    /** Canonical per-org key (`CM-ADR-007`); optional on the wire, always server-filled. */
    adrNumber: z.number().int().positive().optional(),
    adrKey: z.string().optional(),
    displayTitle: z.string().nullable(),
    lifecycleStatus: z.string(),
    summaryExcerpt: z.string().nullable(),
    updatedAt: z.string(),
    scopeLevel: z.string().optional(),
    repositoryId: z.string().uuid().nullable().optional(),
    stackProfileId: z.string().uuid().nullable().optional(),
});
export const standardsCatalogCodingStandardRowSchema = z.object({
    id: z.string().uuid(),
    displayTitle: z.string(),
    lifecycleStatus: z.string(),
    summaryExcerpt: z.string().nullable(),
    updatedAt: z.string(),
    scopeLevel: z.string().optional(),
    repositoryId: z.string().uuid().nullable().optional(),
    stackProfileId: z.string().uuid().nullable().optional(),
});
export const standardsCatalogBundleSchema = z.object({
    adrs: z.array(standardsCatalogAdrRowSchema),
    codingStandards: z.array(standardsCatalogCodingStandardRowSchema),
    nextCursor: z.string().nullable(),
});
/** DC-388 — `GET …/work-items/:id/standards/applicability` bundle. */
export const standardsApplicabilityItemSchema = z.object({
    artifactKind: artifactKindSchema,
    artifactId: z.string().uuid(),
    /** Present for `artifactKind === 'adr'`: canonical per-org key (`CM-ADR-007`). */
    adrKey: z.string().optional(),
    displayTitle: z.string(),
    lifecycleStatus: z.string(),
    sourcesUsed: z.array(applicabilitySourceSchema),
    traceHint: z.string().nullable(),
    /** Present on engineering-memory suggestions — links to durable proposal row. */
    suggestedProposalId: z.string().uuid().optional(),
});
export const engineeringMemoryApplicabilityStateSchema = z.enum([
    'authoritative_only',
    'suggested_available',
    'pending_scan',
    'not_configured',
]);
export const workItemStandardsApplicabilityBundleSchema = z.object({
    gapState: standardsApplicabilityGapStateSchema,
    items: z.array(standardsApplicabilityItemSchema),
    traceabilityEffective: z.boolean(),
    suggestedItems: z.array(standardsApplicabilityItemSchema).optional(),
    engineeringMemoryState: engineeringMemoryApplicabilityStateSchema.optional(),
});
/** DC-388 — `GET …/work-items/:id/governance-routing` (open decisions only). */
export const governanceRoutingOpenDecisionSchema = z.object({
    id: z.string().uuid(),
    kind: governanceRoutingKindSchema,
});
export const workItemGovernanceRoutingStatusSchema = z.object({
    openDecisions: z.array(governanceRoutingOpenDecisionSchema),
});
/** DC-388 — coding standard detail (`GET …/govern/coding-standards/:id`). */
export const codingStandardDetailResponseSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    displayTitle: z.string(),
    lifecycleStatus: z.string(),
    summaryExcerpt: z.string().nullable(),
    acceptedAt: z.string().nullable(),
    updatedAt: z.string(),
    scopeLevel: z.string().optional(),
    repositoryId: z.string().uuid().nullable().optional(),
    stackProfileId: z.string().uuid().nullable().optional(),
    provenance: z
        .object({
        origin: z.string().nullable(),
        policyProposalId: z.string().uuid().nullable(),
        reviewRuleId: z.string().uuid().nullable(),
    })
        .optional(),
});
//# sourceMappingURL=governance-standards-workflows.js.map